import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { eventDetailPath } from "./eventRoutes";
import { normalizeEventRecord } from "./eventViewModel";

export const BUSINESS_NETWORK_SERIES = "urban-unity-business-network";
export const BUSINESS_NETWORK_EVENT_TYPE = "business-network";
export const BUSINESS_NETWORK_TIMEZONE = "Europe/London";
export const OCTOBER_BUSINESS_NETWORK_EVENT_ID = "business-network-2026-10-28";
export const OCTOBER_BUSINESS_NETWORK_EVENT_SLUG =
  "urban-unity-business-network-october-2026";
export const OCTOBER_BUSINESS_NETWORK_EVENT_PATH =
  `/events/${OCTOBER_BUSINESS_NETWORK_EVENT_SLUG}--${OCTOBER_BUSINESS_NETWORK_EVENT_ID}`;

function clean(value) {
  return String(value || "").trim();
}

function londonParts(value, options = {}) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return {};
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_NETWORK_TIMEZONE,
    ...options,
  })
    .formatToParts(date)
    .reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
}

export function londonDateKey(value = new Date()) {
  const parts = londonParts(value, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return parts.year && parts.month && parts.day
    ? `${parts.year}-${parts.month}-${parts.day}`
    : "";
}

export function businessNetworkEventDateKey(event = {}) {
  const direct = clean(event.startDate || event.date);
  if (/^\d{4}-\d{2}-\d{2}$/.test(direct)) return direct;

  const value = event.eventDate;
  if (value?.toDate) return londonDateKey(value.toDate());
  if (value instanceof Date || value) return londonDateKey(value);
  return "";
}

function eventSortKey(event = {}) {
  const date = businessNetworkEventDateKey(event);
  const time = clean(event.startTime || event.time || "00:00");
  return `${date}T${/^\d{2}:\d{2}$/.test(time) ? time : "00:00"}`;
}

export function isBusinessNetworkEvent(event = {}) {
  return (
    clean(event.eventSeries).toLowerCase() === BUSINESS_NETWORK_SERIES ||
    clean(event.eventType).toLowerCase() === BUSINESS_NETWORK_EVENT_TYPE ||
    clean(event.type).toLowerCase() === BUSINESS_NETWORK_EVENT_TYPE
  );
}

export function isBusinessNetworkRegistrationOpen(event = {}, now = new Date()) {
  const dateKey = businessNetworkEventDateKey(event);
  const today = londonDateKey(now);
  const lifecycle = clean(event.lifecycleStatus || event.eventLifecycle).toLowerCase();
  const closesAt = businessNetworkEventEndDate(event);
  return (
    isBusinessNetworkEvent(event) &&
    event.status === "approved" &&
    event.registrationOpen === true &&
    dateKey >= today &&
    (!closesAt || closesAt.getTime() > now.getTime()) &&
    !["cancelled", "completed", "past"].includes(lifecycle)
  );
}

export function partitionBusinessNetworkEvents(events = [], now = new Date()) {
  const today = londonDateKey(now);
  const records = events
    .map((event) => normalizeEventRecord(event))
    .filter((event) => event.status === "approved" && isBusinessNetworkEvent(event))
    .filter((event) => {
      const lifecycle = clean(event.lifecycleStatus || event.eventLifecycle).toLowerCase();
      return lifecycle !== "cancelled";
    });

  const upcoming = records
    .filter((event) => {
      const lifecycle = clean(event.lifecycleStatus || event.eventLifecycle).toLowerCase();
      const dateKey = businessNetworkEventDateKey(event);
      const closesAt = businessNetworkEventEndDate(event);
      return dateKey
        && dateKey >= today
        && (!closesAt || closesAt.getTime() > now.getTime())
        && !["completed", "past"].includes(lifecycle);
    })
    .sort((a, b) => eventSortKey(a).localeCompare(eventSortKey(b), "en-GB"));

  const previous = records
    .filter((event) => {
      const lifecycle = clean(event.lifecycleStatus || event.eventLifecycle).toLowerCase();
      const dateKey = businessNetworkEventDateKey(event);
      const closesAt = businessNetworkEventEndDate(event);
      return ["completed", "past"].includes(lifecycle)
        || (dateKey && dateKey < today)
        || Boolean(closesAt && closesAt.getTime() <= now.getTime());
    })
    .sort((a, b) => eventSortKey(b).localeCompare(eventSortKey(a), "en-GB"));

  return {
    nextEvent: upcoming[0] || null,
    futureEvents: upcoming.slice(1),
    previousEvents: previous,
  };
}

function dateFromKey(dateKey) {
  const match = clean(dateKey).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match
    ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12))
    : null;
}

export function businessNetworkEventEndDate(event = {}) {
  const dateKey = businessNetworkEventDateKey(event);
  const dateMatch = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = clean(event.endTime || event.startTime || event.time)
    .match(/^(\d{2}):(\d{2})$/);
  if (!dateMatch || !timeMatch) return null;
  const targetUtc = Date.UTC(
    Number(dateMatch[1]),
    Number(dateMatch[2]) - 1,
    Number(dateMatch[3]),
    Number(timeMatch[1]),
    Number(timeMatch[2])
  );
  let candidate = targetUtc;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const parts = londonParts(candidate, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    const renderedAsUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute)
    );
    candidate -= renderedAsUtc - targetUtc;
  }
  return new Date(candidate);
}

export function formatBusinessNetworkEventDate(event = {}) {
  const date = dateFromKey(businessNetworkEventDateKey(event));
  if (!date) return "Date to be confirmed";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_NETWORK_TIMEZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })
    .format(date)
    .replace(",", "");
}

function formatTimeValue(value) {
  const match = clean(value).match(/^(\d{2}):(\d{2})$/);
  if (!match) return clean(value);
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const suffix = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

export function formatBusinessNetworkEventTime(event = {}) {
  const start = formatTimeValue(event.startTime || event.time);
  const end = formatTimeValue(event.endTime);
  if (!start) return "Time to be confirmed";
  return end ? `${start}–${end}` : start;
}

function comparableAddressPart(value) {
  return clean(value).toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function formatBusinessNetworkEventAddress(
  event = {},
  { includeVenue = false } = {}
) {
  const venue = clean(event.venueName || event.location);
  const postcode = clean(event.postcode || event.venuePostcode);
  const storedAddress = clean(event.address || event.venueAddress);
  const address = storedAddress || [event.addressLine1, event.area, event.city]
    .map(clean)
    .filter(Boolean)
    .join(", ");
  const postcodeAlreadyIncluded =
    postcode && comparableAddressPart(address).includes(comparableAddressPart(postcode));
  const addressWithPostcode = [
    address,
    postcodeAlreadyIncluded ? "" : postcode,
  ]
    .filter(Boolean)
    .join(", ");

  return [includeVenue ? venue : "", addressWithPostcode]
    .filter(Boolean)
    .join(", ");
}

export function businessNetworkEventPath(event = {}) {
  const preservedPath = clean(event.publicPath || event.legacyPath);
  if (preservedPath.startsWith("/") && !preservedPath.startsWith("//")) {
    return preservedPath;
  }
  return eventDetailPath(event) || "/business-network";
}

function approvedImageUrl(value) {
  const url = clean(value);
  if (url.startsWith("/") && !url.startsWith("//") && !url.includes("\\")) {
    return url;
  }
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.href : "";
  } catch {
    return "";
  }
}

export function getPublishedBusinessNetworkHighlights(event = {}) {
  const highlights = event.highlights && typeof event.highlights === "object"
    ? event.highlights
    : {};
  const published = event.highlightsPublished === true || highlights.published === true;
  if (!published || event.status !== "approved") return null;

  const rawImages = Array.isArray(highlights.images)
    ? highlights.images
    : Array.isArray(event.highlightImages)
    ? event.highlightImages
    : [];
  const images = rawImages
    .map((image, index) => {
      const record = typeof image === "string" ? { url: image } : image || {};
      const url = approvedImageUrl(record.url || record.src || record.imageUrl);
      if (!url || record.approved === false) return null;
      return {
        url,
        alt: clean(record.alt) || `${event.title || "Business Network event"} highlight ${index + 1}`,
        caption: clean(record.caption),
      };
    })
    .filter(Boolean)
    .slice(0, 12);

  const summary = clean(highlights.summary || event.highlightSummary);
  if (!summary && images.length === 0) return null;
  return { summary, images };
}

export function subscribeToBusinessNetworkEvents(onData, onError) {
  const approvedEvents = query(
    collection(db, "events"),
    where("status", "==", "approved")
  );
  return onSnapshot(
    approvedEvents,
    (snapshot) => {
      const events = snapshot.docs
        .map((item) => normalizeEventRecord(item.id, item.data()))
        .filter(isBusinessNetworkEvent);
      onData(events);
    },
    onError
  );
}
